// src/shared/services/UserManagementService.js
// Complete user management system for Actualizer2v1

import { FirebaseService } from './FirebaseService';
import {
  collection,
  doc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  writeBatch
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const CLAIM_REFRESH_ATTEMPTS = 20;
const CLAIM_REFRESH_DELAY_MS = 500;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * UserManagementService - Complete user lifecycle and permissions
 */
class UserManagementServiceClass {
  constructor() {
    this.firestore = null;
    this.initialized = false;
  }

  async initialize() {
    if (this.initialized) return;
    await FirebaseService.initialize();
    this.firestore = FirebaseService.getFirestore();
    this.initialized = true;
    console.log('✅ UserManagementService initialized');
  }

  async ensureInitialized() {
    if (!this.initialized) {
      await this.initialize();
    }
  }

  // =============================================================================
  // MISSING METHODS FOR UserManagement.jsx
  // =============================================================================

  /**
   * Get all users across all tenants (for platform owner)
   */
  async getAllUsers() {
    await this.ensureInitialized();

    // CHA-97 / PR-C: direct userProfiles query blocked at rules layer.
    // Platform-admin-only callable composes roleAssignments + Firebase
    // Auth identity into the canonical user shape.
    //
    // Returned shape (no longer includes legacy `createdAt`/`updatedAt`/
    // `lastLogin` from userProfiles — those fields were never load-bearing
    // post-CHA-97; admin.auth().getUsers() provides identity, role tuple
    // comes from roleAssignments):
    //   { uid, email, displayName, disabled, emailVerified,
    //     roleId, tenantId, assignedBy, assignedAt, _orphan? }
    console.log('👥 Getting all users via listAllUsers callable...');
    const fn = httpsCallable(getFunctions(), 'listAllUsers');
    const result = await fn({});
    const users = result.data?.users || [];
    console.log(`✅ Retrieved ${users.length} total users`);
    return users;
  }

  /**
   * List all users for a specific tenant.
   *
   * CHA-97 / PR-C: tenant-scoped helper that calls the listUsers
   * callable. Replaces the pre-PR-C pattern of `getAllUsers()` +
   * client-side filter by `companyId`, which both leaked all-tenants
   * data to the client and broke at the rules layer post-deploy.
   *
   * Returned shape: same as getAllUsers / listAllUsers — see those
   * methods' docs.
   *
   * @param {string} tenantId
   * @returns {Promise<Array>}
   */
  async listTenantUsers(tenantId) {
    if (!tenantId) throw new Error('listTenantUsers requires tenantId');
    await this.ensureInitialized();
    console.log(`👥 Listing users for tenant ${tenantId} via listUsers callable`);
    const fn = httpsCallable(getFunctions(), 'listUsers');
    const result = await fn({ tenantId });
    return result.data?.users || [];
  }

  /**
   * Get all pending invitations
   */
  async getPendingInvitations() {
    // CHA-125 (Gap A): read moves server-side so firestore.rules can deny all
    // client reads. The `listPendingInvitations` callable is admin-gated and
    // tenant-scoped (a tenant admin only ever sees their own tenant), closing
    // the cross-tenant read leak. Same array shape (createdAt/expiresAt Dates).
    await this.ensureInitialized();

    try {
      console.log('📧 Getting pending invitations...');
      const fn = httpsCallable(getFunctions(), 'listPendingInvitations');
      const result = await fn({});
      const invitations = (result.data?.invitations || []).map((inv) => ({
        ...inv,
        createdAt: inv.createdAt ? new Date(inv.createdAt) : null,
        expiresAt: inv.expiresAt ? new Date(inv.expiresAt) : null,
      }));

      console.log(`✅ Retrieved ${invitations.length} pending invitations`);
      return invitations;

    } catch (error) {
      console.error('❌ Failed to get pending invitations:', error);
      throw error;
    }
  }

  /**
   * Get user statistics for dashboard
   */
  async getUserStats() {
    await this.ensureInitialized();

    try {
      console.log('📊 Getting user statistics...');
      
      const [users, invitations] = await Promise.all([
        this.getAllUsers(),
        this.getPendingInvitations()
      ]);

      // CHA-97 / PR-C: shape from listAllUsers callable is the canonical
      // user shape. Status is sourced from Firebase Auth's `disabled`
      // boolean. recentlyActive metric removed (lastLogin field dropped
      // per CHA-97 Decision 3 — Cloud Functions / Cloud Auth signin
      // events are the audit; no client-readable lastLogin).
      // roleBreakdown groups by `roleId` (canonical field) instead of
      // the legacy `role`.
      const stats = {
        total: users.length,
        active: users.filter(u => !u.disabled).length,
        inactive: users.filter(u => u.disabled === true).length,
        pendingInvitations: invitations.length,
        roleBreakdown: users.reduce((acc, user) => {
          if (!user.roleId) return acc;
          acc[user.roleId] = (acc[user.roleId] || 0) + 1;
          return acc;
        }, {}),
      };

      console.log('✅ User statistics calculated:', stats);
      return stats;

    } catch (error) {
      console.error('❌ Failed to get user statistics:', error);
      throw error;
    }
  }

  /**
   * Create user invitation (platform admin version)
   */
  async createUserInvitation(inviteData) {
    await this.ensureInitialized();

    try {
      console.log('📧 Creating user invitation:', inviteData);

      // Generate invitation
      const invitationId = `inv_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
      
      const invitation = {
        id: invitationId,
        email: inviteData.email,
        name: inviteData.name || '',
        companyId: inviteData.companyId,
        role: inviteData.role || 'user',
        invitedBy: inviteData.invitedBy || 'platform_admin',
        status: 'pending',
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
        permissions: this.getRolePermissions(inviteData.role || 'user'),
        message: inviteData.message || '',
        acceptedAt: null,
        acceptedBy: null
      };

      // Store invitation
      const docRef = doc(this.firestore, 'user_invitations', invitationId);
      await setDoc(docRef, invitation);

      // Generate invitation link
      const baseUrl = window.location.origin;
      const invitationLink = `${baseUrl}/invite/${invitationId}`;

      console.log('✅ User invitation created:', invitationId);
      return {
        invitation,
        invitationLink
      };

    } catch (error) {
      console.error('❌ Failed to create user invitation:', error);
      throw error;
    }
  }

  /**
   * Update user role.
   *
   * CHA-97 / PR-C: signature changed from `(userId, newRole)` to
   * `(userId, tenantId, newRole)` — tenantId is required by the
   * assignUserRole callable and isn't derivable client-side post-rules-
   * lockdown. Callers (UserManagement.jsx, TenantUserManagement.jsx)
   * pass `user.companyId` as tenantId.
   *
   * The legacy `permissions = null` 3rd arg is dropped — permissions
   * are now computed downstream from the role via getRolePermissions
   * (no longer persisted to Firestore per CHA-97).
   *
   * Returns the callable response, not the full user profile (which is
   * no longer readable post-CHA-97 — Ambiguity 2 decision).
   *
   * @param {string} userId
   * @param {string|null} tenantId - tenant slug, or null for global roles
   * @param {string} newRole - the role to assign (validated server-side)
   * @returns {Promise<object>} { ok, targetUid, tenantId, roleId }
   */
  async updateUserRole(userId, tenantId, newRole) {
    await this.ensureInitialized();

    console.log('🔄 Updating user role:', userId, 'to', newRole, 'tenant:', tenantId);
    const functions = getFunctions();
    const assignUserRole = httpsCallable(functions, 'assignUserRole');
    const result = await assignUserRole({
      targetUid: userId,
      tenantId,
      roleId: newRole,
    });
    return result.data;
  }

  // CHA-97 / PR-C (Decision D-3): assignUserToTenant deleted — zero
  // callers in the codebase, dead-but-not-yet-removed. Callers who
  // need this behavior should use the assignUserRole callable directly.

  /**
   * Deactivate user.
   *
   * CHA-97 / PR-C (Decision D-1): routes through the setUserStatus
   * callable. Signature changed from `(userId)` to `(userId, tenantId)`
   * — tenantId required by the callable, not derivable client-side
   * post-rules-lockdown.
   *
   * The legacy `deactivatedAt` timestamp is dropped — setUserStatus
   * writes its own `updatedAt`/`updatedBy` attribution.
   *
   * Trailing `getUserProfile(userId)` dropped per Ambiguity 2.
   *
   * @param {string} userId
   * @param {string} tenantId
   * @returns {Promise<object>} { ok, targetUid, tenantId, status: 'inactive' }
   */
  async deactivateUser(userId, tenantId) {
    await this.ensureInitialized();

    console.log('🔒 Deactivating user:', userId, 'tenant:', tenantId);
    const functions = getFunctions();
    const setUserStatus = httpsCallable(functions, 'setUserStatus');
    const result = await setUserStatus({
      targetUid: userId,
      tenantId,
      status: 'inactive',
    });
    return result.data;
  }

  /**
   * Reactivate user.
   *
   * CHA-97 / PR-C (Decision D-1): mirror of deactivateUser — routes
   * through setUserStatus with `status: 'active'`. Same signature
   * change rationale.
   *
   * @param {string} userId
   * @param {string} tenantId
   * @returns {Promise<object>} { ok, targetUid, tenantId, status: 'active' }
   */
  async reactivateUser(userId, tenantId) {
    await this.ensureInitialized();

    console.log('🔓 Reactivating user:', userId, 'tenant:', tenantId);
    const functions = getFunctions();
    const setUserStatus = httpsCallable(functions, 'setUserStatus');
    const result = await setUserStatus({
      targetUid: userId,
      tenantId,
      status: 'active',
    });
    return result.data;
  }

  // CHA-97 / PR-C (Decision D-3): UMS.deleteUser deleted — zero callers
  // in the codebase. Use the platform-side `deleteUser` callable
  // (functions/platform/userManagement.js) directly if you need to delete
  // a user. The callable is gated by tenant-admin or platform-admin and
  // atomically deletes both userProfiles and roleAssignments docs.

  /**
   * Get available roles for dropdowns
   */
  getAvailableRoles() {
    return [
      { value: 'viewer', label: 'Viewer', description: 'Read-only access to data and reports' },
      { value: 'user', label: 'User', description: 'Can create and edit data, view reports' },
      { value: 'manager', label: 'Manager', description: 'Full data access, can create and share reports' },
      { value: 'admin', label: 'Administrator', description: 'Full access including user management' }
    ];
  }

  // =============================================================================
  // USER ROLE DEFINITIONS & PERMISSIONS (existing methods)
  // =============================================================================

  /**
   * Get default permissions for each role
   */
  getRolePermissions(role) {
    const rolePermissions = {
      platform_owner: {
        // Platform owner has ALL permissions
        platform: ['all'],
        tenant: ['all'], 
        data: ['all'],
        reports: ['all'],
        admin: ['all']
      },
      
      admin: {
        // Tenant admin has full access to their tenant
        platform: [],
        tenant: ['manage_users', 'manage_settings', 'view_analytics'],
        data: ['read', 'write', 'delete', 'export'],
        reports: ['read', 'write', 'share', 'create'],
        admin: ['invite_users', 'manage_roles']
      },
      
      manager: {
        // Manager has most features except user management
        platform: [],
        tenant: ['view_analytics'],
        data: ['read', 'write', 'export'],
        reports: ['read', 'write', 'share', 'create'],
        admin: []
      },
      
      user: {
        // Regular user has basic access
        platform: [],
        tenant: [],
        data: ['read', 'write'],
        reports: ['read', 'create'],
        admin: []
      },
      
      viewer: {
        // Viewer has read-only access
        platform: [],
        tenant: [],
        data: ['read'],
        reports: ['read'],
        admin: []
      }
    };

    return rolePermissions[role] || rolePermissions.user;
  }

  /**
   * Get menu items available for each role
   */
  getRoleMenuItems(role) {
    const allMenuItems = [
      { id: 'dashboard', name: 'Dashboard', icon: 'BarChart3', path: '/dashboard', requiredPermission: ['data', 'read'] },
      { id: 'data_management', name: 'Data Management', icon: 'Database', path: '/data', requiredPermission: ['data', 'read'] },
      { id: 'file_uploads', name: 'File Uploads', icon: 'Upload', path: '/uploads', requiredPermission: ['data', 'write'] },
      { id: 'reports', name: 'Reports', icon: 'FileText', path: '/reports', requiredPermission: ['reports', 'read'] },
      { id: 'analytics', name: 'Analytics', icon: 'TrendingUp', path: '/analytics', requiredPermission: ['tenant', 'view_analytics'] },
      { id: 'user_management', name: 'User Management', icon: 'Users', path: '/users', requiredPermission: ['admin', 'invite_users'] },
      { id: 'settings', name: 'Settings', icon: 'Settings', path: '/settings', requiredPermission: ['tenant', 'manage_settings'] }
    ];

    const userPermissions = this.getRolePermissions(role);
    
    return allMenuItems.filter(item => {
      const [area, action] = item.requiredPermission;
      
      // Platform owner sees everything
      if (role === 'platform_owner') return true;
      
      // Check if user has required permission
      return userPermissions[area] && 
             (userPermissions[area].includes('all') || userPermissions[area].includes(action));
    });
  }

  // =============================================================================
  // USER INVITATION SYSTEM (existing methods)
  // =============================================================================

  /**
   * Invite user to tenant (called by tenant admin or platform owner)
   */
  async inviteUserToTenant(inviteData) {
    await this.ensureInitialized();

    try {
      console.log('📧 Creating user invitation:', inviteData);

      // Validate current user can invite. CHA-97 / PR-C: read role + tenant
      // from claims (getIdTokenResult) instead of the now-locked userProfiles
      // collection. The backend invitation callable (future CHA) is the
      // authoritative authz check; this is a UX guard only.
      //
      // Codex Round 4 P1 #2: claims are minted/consumed as FLAT
      // (claims.roleId / claims.tenantId), not nested under claims.active.
      // See App.jsx:83-125 for the canonical shape. The previous nested
      // read returned null for both fields and canInviteUsers rejected
      // every legitimate admin.
      const currentUser = FirebaseService.getCurrentUser();
      const tokenResult = await currentUser.getIdTokenResult();
      const claims = tokenResult.claims || {};
      const roleId = claims.roleId || null;
      const tenantId = claims.tenantId || null;
      // CodeRabbit Pro Round 3 Major: include `permissions` so
      // canInviteUsers → hasPermission can resolve the admin/invite_users
      // gate. Fix 2 in 8890ade synthesized the profile but dropped the
      // permissions field, causing every legitimate admin to be rejected.
      const currentProfile = {
        uid: currentUser.uid,
        email: currentUser.email,
        role: roleId,
        roleId,
        companyId: tenantId,
        tenantId,
        permissions: this.getRolePermissions(roleId || 'user'),
      };

      if (!this.canInviteUsers(currentProfile, inviteData.companyId)) {
        throw new Error('You do not have permission to invite users to this tenant');
      }

      // Generate invitation
      const invitationId = `inv_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
      
      const invitation = {
        id: invitationId,
        email: inviteData.email,
        companyId: inviteData.companyId,
        role: inviteData.role || 'user',
        invitedBy: currentUser.email,
        invitedByUid: currentUser.uid,
        status: 'pending',
        createdAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days
        permissions: this.getRolePermissions(inviteData.role || 'user'),
        message: inviteData.message || '',
        acceptedAt: null,
        acceptedBy: null
      };

      // Store invitation
      const docRef = doc(this.firestore, 'user_invitations', invitationId);
      await setDoc(docRef, invitation);

      console.log('✅ User invitation created:', invitationId);
      return invitation;

    } catch (error) {
      console.error('❌ Failed to create user invitation:', error);
      throw error;
    }
  }

  /**
   * Get pending invitation for user
   */
  async getPendingUserInvitation(email) {
    // CHA-125 (Gap A): read moves server-side so firestore.rules can deny all
    // client reads (closing the cross-tenant read leak). The callable derives
    // the email from the Auth token — a client cannot read another user's
    // invitation. The `email` arg is retained for signature compatibility but
    // is NOT trusted by the server. Returns the invitation (with `.company`)
    // or null when there is no valid pending invitation.
    await this.ensureInitialized();

    try {
      console.log('🔍 Checking for pending user invitation:', email);
      const fn = httpsCallable(getFunctions(), 'getPendingUserInvitation');
      const result = await fn({});
      return result.data?.invitation || null;
    } catch (error) {
      console.error('❌ No valid user invitation found:', error);
      throw error;
    }
  }

  /**
   * Accept user invitation
   */
  async acceptUserInvitation(invitationId, _userData) {
    await this.ensureInitialized();

    // CHA-97 / PR-C fix-up (Codex Round 1 P1-3): the previous body
    // wrote the role via `saveUserProfile → assignUserRole callable`,
    // which requires an admin caller. Invitees accepting their own
    // invitation are not admins — the call would fail
    // `permission-denied`. Route through the `acceptTenantInvitation`
    // callable instead, which validates by Auth-token email matching
    // the invitation's email (server-side). The callable handles
    // expiry, status, role-assignment write, and invitation update
    // in one atomic flow. The β-1 callable patch accepts both the
    // new schema (tenantId, roleId) and the legacy schema
    // (companyId, role) so existing pending invitations still work.
    console.log('✅ Accepting user invitation via callable:', invitationId);
    const functions = getFunctions();
    const acceptCallable = httpsCallable(functions, 'acceptTenantInvitation');
    const result = await acceptCallable({ invitationId });

    // Codex Round 4 P2 #3: App.handleUserInvitationAccepted treats this
    // return value as a user profile and reads `.mode` + `.companyId`.
    // The callable's raw response is only `{ ok, tenantId, roleId,
    // acceptedAt }`, which would set appMode/selectedTenant to undefined
    // and leave the app in a broken state. Map to the legacy profile
    // shape so the downstream consumer sees the same fields the
    // claims-derived path (App.jsx:88-115) produces. Both legacy field
    // names (role / companyId) and canonical names (roleId / tenantId)
    // are included for downstream consistency.
    //
    const currentUser = FirebaseService.getCurrentUser();
    await this.waitForAcceptedInvitationClaims(currentUser, result.data.tenantId, result.data.roleId);
    return {
      uid: currentUser.uid,
      email: currentUser.email,
      mode: 'tenant',
      role: result.data.roleId,
      roleId: result.data.roleId,
      companyId: result.data.tenantId,
      tenantId: result.data.tenantId,
    };
  }

  async waitForAcceptedInvitationClaims(currentUser, tenantId, roleId) {
    if (!currentUser || typeof currentUser.getIdTokenResult !== 'function') {
      throw new Error('Cannot refresh invitation claims: no signed-in user');
    }

    for (let attempt = 1; attempt <= CLAIM_REFRESH_ATTEMPTS; attempt += 1) {
      const tokenResult = await currentUser.getIdTokenResult(true);
      const claims = tokenResult.claims || {};
      if (claims.tenantId === tenantId && claims.roleId === roleId) {
        return claims;
      }
      if (attempt < CLAIM_REFRESH_ATTEMPTS) {
        await sleep(CLAIM_REFRESH_DELAY_MS);
      }
    }

    throw new Error('Invitation accepted, but tenant access is not ready yet. Please try again in a moment.');
  }

  // =============================================================================
  // USER PROFILE MANAGEMENT (existing methods)
  // =============================================================================

  /**
   * Save/create user role assignment.
   *
   * CHA-97 / PR-C: post-rules-lockdown, this method only persists the
   * role/tenant tuple (via the assignUserRole callable). Non-role fields
   * on profileData (name, dataFilters, mode, status, etc.) were previously
   * written to userProfiles but are intentionally dropped — see CHA-117
   * for the broader profile-field migration.
   *
   * @param {object} profileData - must include `uid` + `roleId` (or legacy `role`).
   *                                companyId/tenantId optional; defaults to null (global role).
   * @returns {Promise<object>} the assignUserRole callable response
   *                            ({ ok, targetUid, tenantId, roleId })
   */
  async saveUserProfile(profileData) {
    await this.ensureInitialized();

    if (!profileData?.uid) {
      throw new Error('saveUserProfile requires profileData.uid');
    }
    const roleId = profileData.roleId || profileData.role;
    if (!roleId) {
      throw new Error('saveUserProfile requires roleId (or legacy role) on profileData');
    }
    // CodeRabbit Pro Round 3 Minor: prefer canonical `tenantId` over
    // legacy `companyId` (matches updateUserProfile's precedence).
    const tenantId = profileData.tenantId ?? profileData.companyId ?? null;

    console.log('💾 Saving user role assignment:', profileData.uid);
    const functions = getFunctions();
    const assignUserRole = httpsCallable(functions, 'assignUserRole');
    const result = await assignUserRole({
      targetUid: profileData.uid,
      tenantId,
      roleId,
    });
    return result.data;
  }

  /**
   * Update user role assignment.
   *
   * CHA-97 / PR-C Ambiguity 3 strict refactor: this method ONLY accepts
   * role/tenant fields. Non-role fields (status, lastLogin, name, etc.)
   * are rejected with a clear error pointing at the right callable.
   *
   * - Status updates → use deactivateUser / reactivateUser (which now
   *   route through the setUserStatus callable)
   * - Profile-field updates (name, etc.) → blocked, see CHA-117
   * - lastLogin telemetry → dropped per Decision 3 (CHA-97 audit)
   *
   * Returns the assignUserRole callable response, not the full user
   * profile (which is no longer readable post-CHA-97 for non-self callers).
   *
   * @param {string} uid - the user being updated
   * @param {object} updates - must contain `roleId`/`role` AND `tenantId`/`companyId`
   * @returns {Promise<object>} { ok, targetUid, tenantId, roleId }
   */
  async updateUserProfile(uid, updates) {
    await this.ensureInitialized();

    const { roleId, role, tenantId, companyId, ...nonRoleFields } = updates || {};
    if (Object.keys(nonRoleFields).length > 0) {
      throw new Error(
        `updateUserProfile only accepts role/tenant fields post-CHA-97. ` +
        `Unsupported fields: ${Object.keys(nonRoleFields).join(', ')}. ` +
        `See CHA-97 (role writes via assignUserRole) / CHA-117 (profile fields) for migration.`
      );
    }
    const resolvedRoleId = roleId || role;
    const resolvedTenantId = tenantId !== undefined ? tenantId : companyId;
    if (!resolvedRoleId) {
      throw new Error('updateUserProfile requires roleId (or legacy role)');
    }
    if (resolvedTenantId === undefined) {
      throw new Error('updateUserProfile requires tenantId (or legacy companyId; pass null for global roles)');
    }

    console.log('📝 Updating user role assignment:', uid);
    const functions = getFunctions();
    const assignUserRole = httpsCallable(functions, 'assignUserRole');
    const result = await assignUserRole({
      targetUid: uid,
      tenantId: resolvedTenantId,
      roleId: resolvedRoleId,
    });
    return result.data;
  }

  // =============================================================================
  // PERMISSION CHECKING METHODS (existing methods)
  // =============================================================================

  /**
   * Check if user has specific permission
   */
  hasPermission(userProfile, area, action) {
    if (!userProfile || !userProfile.permissions) return false;

    // Platform owner has all permissions
    if (userProfile.role === 'platform_owner') return true;

    const areaPermissions = userProfile.permissions[area];
    if (!areaPermissions) return false;

    return areaPermissions.includes('all') || areaPermissions.includes(action);
  }

  /**
   * Check if user can invite other users
   */
  canInviteUsers(userProfile, targetCompanyId = null) {
    // Platform owner can invite to any tenant
    if (userProfile.role === 'platform_owner') return true;

    // User must have admin permissions and be in the same company
    return this.hasPermission(userProfile, 'admin', 'invite_users') && 
           (!targetCompanyId || userProfile.companyId === targetCompanyId);
  }

  /**
   * Check if user can manage another user
   */
  canManageUser(managerProfile, targetUserProfile) {
    // Platform owner can manage anyone
    if (managerProfile.role === 'platform_owner') return true;

    // Must be admin and in same company
    if (!this.hasPermission(managerProfile, 'admin', 'invite_users')) return false;
    if (managerProfile.companyId !== targetUserProfile.companyId) return false;

    // Cannot manage platform owner or users with higher/equal roles
    if (targetUserProfile.role === 'platform_owner') return false;
    if (targetUserProfile.role === 'admin' && managerProfile.role !== 'platform_owner') return false;

    return true;
  }

  /**
   * Check if user can view tenant users
   */
  canViewTenantUsers(userProfile, companyId) {
    // Platform owner can view any tenant's users
    if (userProfile.role === 'platform_owner') return true;

    // Must be admin of the same company
    return this.hasPermission(userProfile, 'admin', 'invite_users') && 
           userProfile.companyId === companyId;
  }

  /**
   * Check if user can access specific menu item
   */
  canAccessMenuItem(userProfile, menuItemId) {
    if (!userProfile || !userProfile.menuItems) return false;
    
    return userProfile.menuItems.some(item => item.id === menuItemId);
  }

  // =============================================================================
  // UTILITY METHODS (existing methods)
  // =============================================================================

  /**
   * Format role name for display
   */
  formatRoleName(role) {
    const roleNames = {
      platform_owner: 'Platform Owner',
      admin: 'Administrator',
      manager: 'Manager',
      user: 'User',
      viewer: 'Viewer'
    };

    return roleNames[role] || role;
  }

  /**
   * Get available roles for a user to assign
   */
  getAssignableRoles(assignerProfile) {
    if (assignerProfile.role === 'platform_owner') {
      return ['admin', 'manager', 'user', 'viewer'];
    }

    if (assignerProfile.role === 'admin') {
      return ['manager', 'user', 'viewer'];
    }

    return [];
  }
}

// Create singleton instance
const UserManagementService = new UserManagementServiceClass();

export { UserManagementService };
